import { inject, Injectable, signal } from '@angular/core';
import { ObjectifReferenceListGQL, UpdateObjectifStepGQL, UpdateObjectifStepInput } from '@lib/models/generated/graphql';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
export interface ObjectifReferenceModel {
  code: string;
  ordre: number;
  libelle: string;
  categorieCode: string;
  categorie: ObjectifCategorieReferenceModel;
}
export interface ObjectifCategorieReferenceModel {
  code: string;
  libelle: string;
  ordre: number;
  objectifs: ObjectifReferenceModel[];
}
@Injectable({ providedIn: 'root' })
export class ObjectifsService {
  private readonly updateObjectifStepGQL = inject(UpdateObjectifStepGQL);
  private readonly objectifReferenceListGQL = inject(ObjectifReferenceListGQL);
  public readonly objectifsRef = signal<ObjectifReferenceModel[]>([]);
  public readonly objectifCategoriesRef = signal<ObjectifCategorieReferenceModel[]>([]);
  private pendingFetch: Promise<void> | null = null;
  constructor() {
    this.fetchObjectifsReference();
  }
  private fetchObjectifsReference(): Promise<void> {
    // Si déjà en mémoire, rien à faire.
    if (this.objectifsRef().length > 0 && this.objectifCategoriesRef().length > 0) {
      return Promise.resolve();
    }
    // Si déjà en cours de chargement, on attend la même requête plutôt que de renvoyer une liste encore vide.
    this.pendingFetch ??= this.loadObjectifsReference().finally(() => (this.pendingFetch = null));
    return this.pendingFetch;
  }
  private async loadObjectifsReference(): Promise<void> {
    const { data } = await firstValueFrom(this.objectifReferenceListGQL.fetch());
    const refs = data!.objectifReferentiels ?? [];
    const categorieMap = new Map<string, ObjectifCategorieReferenceModel>();
    const objectifs: ObjectifReferenceModel[] = refs.map(ref => {
      const categorieCode = ref.categorie?.code ?? ref.categorieCode;
      let categorie = categorieMap.get(categorieCode);
      if (!categorie) {
        categorie = {
          code: categorieCode,
          libelle: ref.categorie?.libelle ?? '',
          ordre: ref.categorie?.ordre ?? 0,
          objectifs: [],
        };
        categorieMap.set(categorieCode, categorie);
      }
      const objectif: ObjectifReferenceModel = {
        code: ref.code,
        ordre: ref.ordre,
        libelle: ref.libelle,
        categorieCode: ref.categorieCode,
        categorie,
      };
      categorie.objectifs.push(objectif);
      return objectif;
    });
    objectifs.sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code));
    const categories = Array.from(categorieMap.values()).sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code));
    categories.forEach(cat => cat.objectifs.sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code)));
    this.objectifsRef.set(objectifs);
    this.objectifCategoriesRef.set(categories);
  }
  public async getObjectifReferences(): Promise<ObjectifReferenceModel[]> {
    if (this.objectifsRef().length === 0) {
      await this.fetchObjectifsReference();
    }
    return this.objectifsRef();
  }
  public async getObjectifCategories(): Promise<ObjectifCategorieReferenceModel[]> {
    if (this.objectifCategoriesRef().length === 0) {
      await this.fetchObjectifsReference();
    }
    return this.objectifCategoriesRef();
  }
  public async updateObjectifs(input: UpdateObjectifStepInput): Promise<void> {
    await firstValueFrom(
      this.updateObjectifStepGQL.mutate({
        variables: { input },
      })
    );
  }
}
